import { useEffect, useRef, useState } from "react";

export type RealtimeMessage = {
  type: string;
  scope?: "customer" | "staff";
  tenantId?: number;
  outletId?: number | null;
  orderId?: string | null;
  payload?: Record<string, unknown>;
  at?: string;
};

type UseRealtimeOptions = {
  enabled?: boolean;
  staff?: boolean;
  customerToken?: string | null;
  orderId?: string | null;
  onEvent: (event: RealtimeMessage) => void;
};

/**
 * Koneksi WebSocket ke /ws. Reconnect otomatis dengan backoff. Saat terputus,
 * halaman boleh memakai polling lambat sebagai fallback.
 */
export function useRealtime({
  enabled = true,
  staff = false,
  customerToken,
  orderId,
  onEvent,
}: UseRealtimeOptions): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const ready = enabled && (staff || Boolean(customerToken && orderId));

  useEffect(() => {
    if (!ready) {
      setConnected(false);
      return;
    }

    let stopped = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | undefined;
    let pingTimer: number | undefined;
    let attempt = 0;

    const params = new URLSearchParams();
    if (staff) {
      params.set("scope", "staff");
    } else {
      params.set("scope", "customer");
      params.set("token", customerToken ?? "");
      params.set("order", orderId ?? "");
    }
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${protocol}//${window.location.host}/ws?${params.toString()}`;

    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(url);

      socket.addEventListener("open", () => {
        attempt = 0;
        setConnected(true);
        pingTimer = window.setInterval(() => {
          try {
            socket?.send(JSON.stringify({ type: "ping" }));
          } catch {
            // reconnect akan menangani koneksi yang sudah mati
          }
        }, 25_000);
      });

      socket.addEventListener("message", (message) => {
        try {
          const event = JSON.parse(String(message.data)) as RealtimeMessage;
          if (event.type === "pong") return;
          if (event.type === "connected") {
            setConnected(true);
            return;
          }
          onEventRef.current(event);
        } catch {
          // Abaikan pesan non-JSON.
        }
      });

      socket.addEventListener("close", (event) => {
        setConnected(false);
        if (pingTimer) {
          window.clearInterval(pingTimer);
          pingTimer = undefined;
        }
        if (stopped || event.code === 4001 || event.code === 4003) return;
        const delay = Math.min(10_000, 1_000 * 2 ** Math.min(attempt++, 4));
        reconnectTimer = window.setTimeout(connect, delay);
      });

      socket.addEventListener("error", () => {
        socket?.close();
      });
    };

    connect();

    return () => {
      stopped = true;
      setConnected(false);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      if (pingTimer) window.clearInterval(pingTimer);
      socket?.close();
    };
  }, [ready, staff, customerToken, orderId]);

  return { connected };
}
